import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getMenuItems } from '../../config/menuItems';
import { logActivitySafe } from '../../utils/logActivitySafe';

const drawerWidth = 300;

const resolveUserRoleLevel = (role?: string) => {
  const code = String(role || '').toUpperCase();
  if (code === 'SUPER_ADMIN') return 1;
  if (code === 'ADMIN') return 2;
  if (code.startsWith('ADMIN_')) return 3;
  if (code.startsWith('STAFF_') || code === 'STAFF' || code === 'FACULTY' || code === 'TEACHER') return 4;
  if (code === 'STUDENT') return 5;
  return 999;
};

const resolveRequiredRoleLevel = (role?: string) => {
  const code = String(role || '').toUpperCase();
  if (code === 'SUPER_ADMIN') return 1;
  if (code === 'ADMIN') return 3;
  if (code === 'STAFF' || code === 'FACULTY' || code === 'TEACHER') return 4;
  if (code === 'STUDENT') return 5;
  return 999;
};

const SidebarItem = ({
  item,
  navigate,
  onLog,
  expanded,
  onToggle,
}: {
  item: any;
  navigate: any;
  onLog: any;
  expanded: boolean;
  onToggle: (path: string) => void;
}) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren && item.children.some((child: any) => location.pathname.startsWith(child.path));
  const isSelfActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  const handleClick = () => {
    if (hasChildren) {
      onToggle(item.path);
    } else {
      navigate(item.path);
      onLog(item);
    }
  };

  const listItemSx = {
    color: '#ffffff',
    '& .MuiListItemIcon-root': {
      color: '#ffffff',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  };

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton 
          onClick={handleClick}
          selected={!hasChildren && isSelfActive}
          sx={listItemSx}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText 
            primary={item.label} 
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isSelfActive || isChildActive ? 600 : 400,
            }}
          />
          {hasChildren ? (expanded ? <ExpandLess /> : <ExpandMore />) : null}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child: any) => (
              child.isHeader ? (
                <ListItem key={child.path} sx={{ pl: 4, py: 0.5 }}>
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'rgba(255, 255, 255, 0.65)',
                    }}
                  />
                </ListItem>
              ) : (
                <ListItem key={child.path} disablePadding sx={{ pl: 4 }}>
                  <ListItemButton
                    onClick={() => {
                      // Prevent toggling the parent; only navigate
                      navigate(child.path);
                      onLog(child);
                    }}
                    selected={location.pathname === child.path}
                    sx={listItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{child.icon}</ListItemIcon>
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === child.path ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * Main Layout Component
 * Sidebar visibility is driven by:
 * 1. Role
 * 2. Enabled school modules (school_modules)
 *
 * Permissions are NOT used here.
 */
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedModulePath, setExpandedModulePath] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Log logout activity explicitly
    logActivitySafe({
      module: "AUTH",
      action: "LOGOUT",
      description: "User logged out"
    });
    
    handleMenuClose();
    logout();
  };

  /**
   * Menu items by role scope
   */
  const roleBasedMenuItems = getMenuItems(user?.role?.code);

  /**
   * Enabled modules for school (from backend - single source of truth)
   * Always read from user.school.enabledModules which is refreshed from backend
   * This will trigger re-render when user state changes
   */
  const enabledModules: string[] =
    user?.school?.enabledModules || [];
  
  // Force re-render when user changes (ensures menu updates after toggle)
  const [, forceUpdate] = useState(0);
  
  useEffect(() => {
    // Listen for user refresh events (triggered after module toggles)
    const handleUserRefresh = () => {
      forceUpdate((prev) => prev + 1);
    };
    window.addEventListener('userRefreshed', handleUserRefresh);
    return () => window.removeEventListener('userRefreshed', handleUserRefresh);
  }, []);
  
  // Re-compute filtered menu items when user or enabledModules change
  useEffect(() => {
    // This ensures menu updates when enabledModules change
  }, [user?.school?.enabledModules, user?.role?.code]);

  /**
   * FINAL menu filtering logic
   */
  const filteredMenuItems = roleBasedMenuItems.filter((item: any) => {
    // 1. Role Scope Check (Global vs School)
    if (item.requiredRoles && user) {
      const userLevel = resolveUserRoleLevel(user.role.code);
      const allowed = item.requiredRoles.some(
        (role: string) =>
          userLevel <= resolveRequiredRoleLevel(role),
      );
      if (!allowed) return false;
    }

    // 2. Global Items (No Module Code) -> Always Show (if role check passed)
    if (!item.moduleCode) {
      return true;
    }

    // 3. Authoritative Visibility Rule (Step 3)
    // const canViewModule = (enabledModules.includes(MODULE_CODE) && (user.role === 'ADMIN' || permissions.includes(REQUIRED_PERMISSION)));
    
    const isModuleEnabled = enabledModules.includes(item.moduleCode);
    const isAdmin = user?.role?.code === 'ADMIN' || user?.role?.code === 'SUPER_ADMIN';
    
    // Use effective permissions from backend (single source of truth)
    const userPermissions = user?.permissions || [];
    
    const hasPermission = item.viewPermission 
        ? userPermissions.includes(item.viewPermission)
        : true; // Fallback for modules without explicit view permission (e.g. Transportation)

    return isModuleEnabled && (isAdmin || hasPermission);
  });

  useEffect(() => {
    const activeGroup = filteredMenuItems.find(
      (item: any) =>
        item.children?.length &&
        (location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`) ||
          item.children.some((child: any) => location.pathname.startsWith(child.path))),
    );
    if (activeGroup?.path) {
      setExpandedModulePath(activeGroup.path);
    }
    // Keep dependency narrow so manual toggling between modules works while staying on a sub-route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const drawer = (
    <div>
      <Toolbar
        sx={{
          backgroundColor: '#1e3a8a',
          color: '#ffffff',
          minHeight: '64px !important',
          display: 'flex',
          alignItems: 'center',
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
              E
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Academix ERP
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
              School Management System
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <List sx={{ px: 1, py: 2 }}>
        {filteredMenuItems.map((item: any) => (
          <SidebarItem
            key={item.path}
            item={item}
            navigate={navigate}
            expanded={expandedModulePath === item.path}
            onToggle={(path: string) =>
              setExpandedModulePath((prev) => (prev === path ? null : path))
            }
            onLog={(i: any) =>
              logActivitySafe({
                module: i.logModule || i.moduleCode,
                action: 'OPEN_MODULE',
                description: `Opened ${i.label} module`,
              })
            }
          />
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          color: '#1f2937',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#1f2937' }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" noWrap sx={{ color: '#1f2937', fontWeight: 600 }}>
              {user?.school?.name || 'Academix ERP'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => navigate('/profile')}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              backgroundColor: '#1e3a8a',
              color: '#ffffff',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
